import React, { createContext, useContext, useState } from 'react';

const translations = {
  en: {
    nav: {
      architecture: 'Architecture',
      predictive: 'Predictive',
      ecosystem: 'Ecosystem',
      contact: 'Contact',
      requestAccess: 'Request Enterprise Access'
    },
    hero: {
      subtitle: 'Enterprise Data Intelligence & Predictive Analytics',
      cta: 'Request Enterprise Access',
      description: 'Next-generation B2B SaaS platform delivering real-time data intelligence for Fortune 500 enterprises'
    },
    architecture: {
      title: 'Data Architecture',
      subtitle: 'Enterprise-Grade Infrastructure',
      description: 'Built on distributed cloud architecture with financial-grade security protocols',
      features: {
        cloud: {
          title: 'Cloud Infrastructure',
          description: 'Multi-region deployment with 99.99% uptime SLA'
        },
        microservices: {
          title: 'Microservices',
          description: 'Containerized services with auto-scaling capabilities'
        },
        security: {
          title: 'Security',
          description: 'SOC 2 Type II compliant with end-to-end encryption'
        },
        distributed: {
          title: 'Distributed Backend',
          description: 'Event-driven architecture processing millions of events'
        }
      }
    },
    predictive: {
      title: 'Predictive Modeling',
      subtitle: 'Advanced Machine Learning',
      description: 'Our neural network algorithms deliver predictive accuracy exceeding 97.3% across financial datasets',
      features: {
        ml: 'Deep Learning Neural Networks',
        realtime: 'Real-time Pattern Recognition',
        accuracy: 'Predictive Accuracy',
        processing: 'Data Processing Speed'
      },
      stats: {
        accuracy: 'Accuracy Rate',
        latency: 'Avg Latency',
        models: 'Active Models',
        datapoints: 'Daily Datapoints'
      }
    },
    ecosystem: {
      title: 'SaaS Ecosystem',
      subtitle: 'Enterprise API Integration',
      description: 'Seamless integration with your existing infrastructure through our RESTful and GraphQL APIs',
      tps: '1.2M TPS',
      tpsLabel: 'Transactions Per Second',
      features: {
        rest: 'RESTful API',
        graphql: 'GraphQL Support',
        webhooks: 'Real-time Webhooks',
        sdk: 'Enterprise SDK'
      }
    },
    footer: {
      copyright: 'Copyright 2026 PAPES US LLC. All Rights Reserved. Pure B2B SaaS.',
      contact: 'Contact'
    },
    modal: {
      title: 'Request Enterprise Access',
      subtitle: 'Complete the form below and our enterprise team will contact you within 24 hours',
      name: 'Full Name',
      email: 'Corporate Email',
      company: 'Company Name',
      message: 'Message (Optional)',
      submit: 'Submit Request',
      success: 'Request submitted successfully!',
      error: 'Error submitting request. Please try again.'
    }
  },
  es: {
    nav: {
      architecture: 'Arquitectura',
      predictive: 'Predictivo',
      ecosystem: 'Ecosistema',
      contact: 'Contacto',
      requestAccess: 'Solicitar Acceso Empresarial'
    },
    hero: {
      subtitle: 'Inteligencia de Datos Empresarial y Análisis Predictivo',
      cta: 'Solicitar Acceso Empresarial',
      description: 'Plataforma B2B SaaS de próxima generación que ofrece inteligencia de datos en tiempo real para empresas Fortune 500'
    },
    architecture: {
      title: 'Arquitectura de Datos',
      subtitle: 'Infraestructura de Nivel Empresarial',
      description: 'Construida sobre arquitectura cloud distribuida con protocolos de seguridad de grado financiero',
      features: {
        cloud: {
          title: 'Infraestructura Cloud',
          description: 'Despliegue multi-región con SLA de 99.99% de disponibilidad'
        },
        microservices: {
          title: 'Microservicios',
          description: 'Servicios containerizados con capacidades de auto-escalado'
        },
        security: {
          title: 'Seguridad',
          description: 'Cumplimiento SOC 2 Tipo II con cifrado de extremo a extremo'
        },
        distributed: {
          title: 'Backend Distribuido',
          description: 'Arquitectura dirigida por eventos procesando millones de eventos'
        }
      }
    },
    predictive: {
      title: 'Modelado Predictivo',
      subtitle: 'Machine Learning Avanzado',
      description: 'Nuestros algoritmos de redes neuronales ofrecen precisión predictiva superior al 97.3% en datasets financieros',
      features: {
        ml: 'Redes Neuronales Deep Learning',
        realtime: 'Reconocimiento de Patrones en Tiempo Real',
        accuracy: 'Precisión Predictiva',
        processing: 'Velocidad de Procesamiento'
      },
      stats: {
        accuracy: 'Tasa de Precisión',
        latency: 'Latencia Promedio',
        models: 'Modelos Activos',
        datapoints: 'Puntos de Datos Diarios'
      }
    },
    ecosystem: {
      title: 'Ecosistema SaaS',
      subtitle: 'Integración API Empresarial',
      description: 'Integración perfecta con su infraestructura existente a través de nuestras APIs RESTful y GraphQL',
      tps: '1.2M TPS',
      tpsLabel: 'Transacciones Por Segundo',
      features: {
        rest: 'API RESTful',
        graphql: 'Soporte GraphQL',
        webhooks: 'Webhooks en Tiempo Real',
        sdk: 'SDK Empresarial'
      }
    },
    footer: {
      copyright: 'Copyright 2026 PAPES US LLC. Todos los Derechos Reservados. Pure B2B SaaS.',
      contact: 'Contacto'
    },
    modal: {
      title: 'Solicitar Acceso Empresarial',
      subtitle: 'Complete el formulario y nuestro equipo empresarial lo contactará en 24 horas',
      name: 'Nombre Completo',
      email: 'Email Corporativo',
      company: 'Nombre de la Empresa',
      message: 'Mensaje (Opcional)',
      submit: 'Enviar Solicitud',
      success: '¡Solicitud enviada exitosamente!',
      error: 'Error al enviar solicitud. Por favor intente de nuevo.'
    }
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'es' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
